import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, ChartBar, Trophy, GameController, TrendUp, Target } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GameStatsPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const targetId = userId || user?.id;
  const [allStats, setAllStats] = useState(null);
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameStats, setGameStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (targetId) fetchData();
  }, [targetId]);

  const fetchData = async () => {
    try {
      const [statsRes, gamesRes] = await Promise.all([
        axios.get(`${API_URL}/api/users/${targetId}/all-game-stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/games`, { withCredentials: true }),
      ]);
      setAllStats(statsRes.data);
      setGames(gamesRes.data.filter(g => g.slug) || []);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  const selectGame = async (slug) => {
    setSelectedGame(slug);
    try {
      const { data } = await axios.get(`${API_URL}/api/games/${slug}/user-stats/${targetId}`, { withCredentials: true });
      setGameStats(data);
    } catch (err) { setGameStats(null); }
  };

  if (loading) return <div className="app-container flex items-center justify-center min-h-screen"><div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin" /></div>;

  const ratings = allStats?.ratings || [];
  const gameStatsMap = allStats?.game_stats || {};

  return (
    <div className="app-container" data-testid="game-stats-page">
      <header className="p-6 pb-3">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              <ChartBar size={24} weight="duotone" className="text-[#00F0FF]" />
              Game Stats
            </h1>
            <p className="text-[#71717A] text-xs">Universal analytics across all games</p>
          </div>
        </div>
      </header>

      <div className="px-6 pb-32 space-y-4">
        {/* Ratings Overview */}
        {ratings.length > 0 && (
          <div>
            <h3 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
              <Trophy size={16} weight="duotone" className="text-[#FACC15]" />
              Ratings
            </h3>
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-6 px-6 scrollbar-none">
              {ratings.map((r, i) => (
                <div key={i} className="min-w-[120px] p-3 rounded-xl bg-[#0F0F16] border border-white/5 flex-shrink-0 text-center"
                  data-testid={`rating-${r.game_type}`}>
                  <h4 className="text-white text-xs font-medium truncate">{r.game_type}</h4>
                  <p className="text-[#FACC15] text-xl font-bold">{r.rating}</p>
                  <div className="flex justify-center gap-2 text-[9px] text-[#71717A]">
                    <span className="text-[#39FF14]">{r.wins}W</span>
                    <span className="text-[#EF4444]">{r.losses}L</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Game Selection */}
        <div>
          <h3 className="text-white text-sm font-semibold mb-2 flex items-center gap-2">
            <GameController size={16} weight="duotone" className="text-[#9D4CDD]" />
            Detailed Stats by Game
          </h3>
          <div className="flex gap-1.5 flex-wrap">
            {games.slice(0, 10).map(g => {
              const hasStats = gameStatsMap[g.slug];
              return (
                <button key={g.slug} onClick={() => selectGame(g.slug)}
                  className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${selectedGame === g.slug ? 'bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] text-white' : hasStats ? 'bg-[#9D4CDD]/10 text-[#9D4CDD] border border-[#9D4CDD]/20' : 'bg-[#181824] text-[#71717A]'}`}
                  data-testid={`game-select-${g.slug}`}>
                  {g.name}
                  {hasStats && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-[#39FF14] inline-block" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Game Stats */}
        {selectedGame && gameStats && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
            <h3 className="text-white text-sm font-semibold flex items-center gap-2">
              <TrendUp size={16} weight="duotone" className="text-[#00F0FF]" />
              {gameStats.game} Stats
            </h3>

            {/* Rating for this game */}
            {gameStats.rating && (
              <div className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 flex items-center justify-between" data-testid="game-rating">
                <span className="text-[#A1A1AA] text-xs">ELO Rating</span>
                <span className="text-[#FACC15] text-lg font-bold">{gameStats.rating.rating}</span>
              </div>
            )}

            {/* Stat Summaries */}
            {gameStats.summaries?.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {gameStats.summaries.map((s, i) => (
                  <div key={i} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5" data-testid={`stat-${s.stat_key}`}>
                    <p className="text-[#71717A] text-[9px]">{s.stat_name}</p>
                    <p className="text-white text-lg font-bold">{typeof s.average === 'number' ? s.average : s.total}</p>
                    <div className="flex gap-2 text-[8px] text-[#A1A1AA]">
                      <span>Total: {s.total}</span>
                      {s.max_val != null && <span>Best: {s.max_val}</span>}
                    </div>
                    <p className="text-[#71717A] text-[8px]">{s.sample_size} matches</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Target size={28} weight="duotone" className="text-[#71717A] mx-auto mb-2" />
                <p className="text-[#71717A] text-xs">No detailed stats yet for this game</p>
                <p className="text-[#A1A1AA] text-[10px]">Record matches with stat tracking to see analytics here</p>
              </div>
            )}
          </motion.div>
        )}

        {/* Games with universal stats */}
        {Object.keys(gameStatsMap).length > 0 && !selectedGame && (
          <div>
            <h3 className="text-white text-sm font-semibold mb-2">Games with Tracked Stats</h3>
            {Object.entries(gameStatsMap).map(([slug, data]) => (
              <button key={slug} onClick={() => selectGame(slug)}
                className="w-full text-left p-3 rounded-xl bg-[#0F0F16] border border-white/5 mb-2 hover:border-[#9D4CDD]/30 transition-all"
                data-testid={`tracked-game-${slug}`}>
                <div className="flex items-center justify-between">
                  <h4 className="text-white text-sm font-medium">{data.game_name}</h4>
                  <span className="text-[#9D4CDD] text-xs">{data.stats.length} stats tracked</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {ratings.length === 0 && Object.keys(gameStatsMap).length === 0 && (
          <div className="text-center py-10">
            <ChartBar size={40} weight="duotone" className="text-[#71717A] mx-auto mb-3" />
            <h3 className="text-white text-sm font-medium mb-1">No Stats Yet</h3>
            <p className="text-[#71717A] text-xs">Play and record games to start tracking your stats!</p>
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
